import { exclusiveEndDate, type InboxAnalyticsGranularity, type InboxAnalyticsSort, type InboxAnalyticsUrlState } from "./inbox-analytics-url-state.ts";
import type { InboxAnalyticsBucket, InboxAnalyticsInbox } from "./inbox-analytics-api.ts";

export type InboxAnalyticsTableBucket = {
  bucketStart: string;
  label: string;
};

export type InboxAnalyticsTableCell = {
  gmail: number;
  legacy: number;
};

export type InboxAnalyticsTableRow = {
  inbox: InboxAnalyticsInbox;
  cells: InboxAnalyticsTableCell[];
};

export type InboxAnalyticsCellPresentation = {
  primary: string;
  legacy: string | null;
  tooltip: string;
};

const ZERO_CELL: InboxAnalyticsTableCell = { gmail: 0, legacy: 0 };

function parseUtcDate(value: string): Date {
  return new Date(`${value}T00:00:00.000Z`);
}

function floorBucket(date: Date, granularity: InboxAnalyticsGranularity): Date {
  const result = new Date(date);
  result.setUTCMinutes(0, 0, 0);
  if (granularity === "hour") return result;
  result.setUTCHours(0);
  if (granularity === "day") return result;
  if (granularity === "week") {
    const daysSinceMonday = (result.getUTCDay() + 6) % 7;
    result.setUTCDate(result.getUTCDate() - daysSinceMonday);
    return result;
  }
  result.setUTCDate(1);
  if (granularity === "month") return result;
  result.setUTCMonth(0);
  return result;
}

function addBucket(date: Date, granularity: InboxAnalyticsGranularity): void {
  if (granularity === "hour") date.setUTCHours(date.getUTCHours() + 1);
  else if (granularity === "day") date.setUTCDate(date.getUTCDate() + 1);
  else if (granularity === "week") date.setUTCDate(date.getUTCDate() + 7);
  else if (granularity === "month") date.setUTCMonth(date.getUTCMonth() + 1);
  else date.setUTCFullYear(date.getUTCFullYear() + 1);
}

function formatBucketLabel(bucketStart: string, granularity: InboxAnalyticsGranularity): string {
  const date = new Date(bucketStart);
  const dateOptions: Intl.DateTimeFormatOptions = { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" };
  if (granularity === "hour") {
    return `${new Intl.DateTimeFormat("en-GB", dateOptions).format(date)}, ${new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "UTC" }).format(date)} UTC`;
  }
  if (granularity === "day") return new Intl.DateTimeFormat("en-GB", dateOptions).format(date);
  if (granularity === "week") return `Week of ${new Intl.DateTimeFormat("en-GB", dateOptions).format(date)}`;
  if (granularity === "month") return new Intl.DateTimeFormat("en-GB", { month: "short", year: "numeric", timeZone: "UTC" }).format(date);
  return new Intl.DateTimeFormat("en-GB", { year: "numeric", timeZone: "UTC" }).format(date);
}

export function buildInboxAnalyticsTableBuckets(filters: Pick<InboxAnalyticsUrlState, "from" | "to" | "granularity">): InboxAnalyticsTableBucket[] {
  const start = floorBucket(parseUtcDate(filters.from), filters.granularity);
  const end = parseUtcDate(exclusiveEndDate(filters.to));
  const buckets: InboxAnalyticsTableBucket[] = [];
  for (const cursor = new Date(start); cursor < end; addBucket(cursor, filters.granularity)) {
    const bucketStart = cursor.toISOString();
    buckets.push({ bucketStart, label: formatBucketLabel(bucketStart, filters.granularity) });
  }
  return buckets;
}

function canonicalBucketStart(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toISOString();
}

function cellForBucket(buckets: InboxAnalyticsBucket[], bucketStart: string): InboxAnalyticsTableCell {
  const bucket = buckets.find((candidate) => canonicalBucketStart(candidate.bucketStart) === bucketStart);
  return bucket ? { gmail: bucket.gmail, legacy: bucket.legacy } : ZERO_CELL;
}

export function presentInboxAnalyticsCell(
  inboxAddress: string,
  bucketLabel: string,
  cell: InboxAnalyticsTableCell,
): InboxAnalyticsCellPresentation {
  const difference = cell.legacy - cell.gmail;
  const signedDifference = difference > 0 ? `+${difference}` : `${difference}`;
  return {
    primary: cell.gmail.toLocaleString("en-GB"),
    legacy: cell.legacy === cell.gmail ? null : cell.legacy.toLocaleString("en-GB"),
    tooltip: [
      bucketLabel,
      `Inbox: ${inboxAddress}`,
      `Gmail: ${cell.gmail.toLocaleString("en-GB")}`,
      `Legacy: ${cell.legacy.toLocaleString("en-GB")}`,
      `Difference (Legacy − Gmail): ${signedDifference}`,
    ].join("\n"),
  };
}

function compareNames(left: InboxAnalyticsTableRow, right: InboxAnalyticsTableRow): number {
  const nameComparison = left.inbox.inboxName.localeCompare(right.inbox.inboxName, "en-GB", { sensitivity: "base" });
  return nameComparison || left.inbox.inboxId.localeCompare(right.inbox.inboxId);
}

export function buildInboxAnalyticsTableRows(
  inboxes: InboxAnalyticsInbox[],
  columns: InboxAnalyticsTableBucket[],
  sort: InboxAnalyticsSort,
  direction: "asc" | "desc",
): InboxAnalyticsTableRow[] {
  const rows = inboxes.map((inbox) => ({ inbox, cells: columns.map((column) => cellForBucket(inbox.buckets, column.bucketStart)) }));
  const sortIndex = columns.findIndex((column) => column.bucketStart === sort);
  const multiplier = direction === "desc" ? -1 : 1;
  rows.sort((left, right) => {
    if (sort === "inbox" || sortIndex < 0) return multiplier * compareNames(left, right);
    const gmailComparison = left.cells[sortIndex].gmail - right.cells[sortIndex].gmail;
    if (gmailComparison) return multiplier * gmailComparison;
    const legacyComparison = left.cells[sortIndex].legacy - right.cells[sortIndex].legacy;
    if (legacyComparison) return multiplier * legacyComparison;
    return compareNames(left, right);
  });
  return rows;
}
